import {
  ChangeEvent,
  ChangeEventHandler,
  FC,
  memo,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {ValidateContext} from './app';
import {EntryData as EntryProps} from './math';

export const Entry: FC<PropsWithChildren<EntryProps>> = ({
  part1,
  part2,
  operator,
  hiddenElement,
  result,
}) => {
  const validateContext = useContext(ValidateContext);

  const validData =
    hiddenElement === 'part1'
      ? part1
      : hiddenElement === 'part2'
      ? part2
      : result;

  const [value, setValue] = useState<string>('');
  const [isEntryValid, setIsEntryValid] = useState<boolean | undefined>(
    undefined
  );

  const [clearField, setClearField] = useState(validateContext.clearFormToggle);
  const [validateField, setValidateField] = useState<boolean | undefined>(
    validateContext.validateToggle
  );

  const onChangeHandler: ChangeEventHandler = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setValue(event.target.value);
    setIsEntryValid(undefined);
  };

  const validateEntry = useCallback(() => {
    const result = Boolean(value !== '' && Number(value) === validData);
    setIsEntryValid(result);
  }, [value, validData]);

  useEffect(() => {
    if (validateContext.validateToggle !== validateField) {
      validateEntry();
      setValidateField(!validateField);
    }
    if (validateContext.clearFormToggle !== clearField) {
      setClearField(!clearField);
      setIsEntryValid(undefined);
      setValue('');
    }
  }, [validateContext, clearField, isEntryValid, validateField, validateEntry]);

  const EntryInput = (
    <input
      className={`mb-2 h-8 w-14 text-center ${
        isEntryValid === undefined
          ? 'border-black'
          : isEntryValid
          ? 'border-green-500 bg-green-200'
          : 'border-red-800 bg-red-200'
      }`}
      type="number"
      value={value}
      onChange={onChangeHandler}
    />
  );

  return (
    <div>
      <label className="mx-0 my-auto flex justify-between">
        <span className="self-baseline">
          {hiddenElement === 'part1' ? EntryInput : part1}
        </span>
        <span className="self-baseline">{operator}</span>
        <span className="self-baseline">
          {hiddenElement === 'part2' ? EntryInput : part2}
        </span>
        <span className="self-baseline">=</span>
        <span className="mr-6 self-baseline">
          {hiddenElement === 'result' ? EntryInput : result}
        </span>
      </label>
    </div>
  );
};

export const MemoizedEntry = memo(Entry);
